'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';

import type { ActiveSession, SessionLimitState } from '../types';

interface UseSessionManagementReturn {
  sessions: ActiveSession[];
  isLoading: boolean;
  error: string | null;
  selectedSessions: Set<string>;
  terminateSession: (sessionId: string) => Promise<void>;
  terminateSelected: () => Promise<void>;
  terminateAll: () => Promise<void>;
  toggleSessionSelection: (sessionId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  upgradeAndContinue: () => Promise<void>;
}

export function useSessionManagement(
  state: SessionLimitState,
): UseSessionManagementReturn {
  const router = useRouter();
  const [sessions, setSessions] = useState<ActiveSession[]>(state.activeSessions);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeRequest = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.accessToken}`,
          ...options.headers,
        },
      });
      return response.json();
    },
    [state.accessToken],
  );

  const terminateSession = useCallback(
    async (sessionId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await makeRequest(`/api/auth/sessions/${sessionId}`, {
          method: 'DELETE',
        });

        if (result.success) {
          setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
          setSelectedSessions((prev) => {
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
          });
        } else {
          setError(result.error || 'Failed to terminate session');
        }
      } catch {
        setError('Failed to terminate session');
      } finally {
        setIsLoading(false);
      }
    },
    [makeRequest],
  );

  const terminateSelected = useCallback(async () => {
    if (selectedSessions.size === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const sessionIds = Array.from(selectedSessions);
      const result = await makeRequest('/api/auth/sessions/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ sessionIds }),
      });

      if (result.success) {
        setSessions((prev) =>
          prev.filter((s) => !selectedSessions.has(s.sessionId)),
        );
        setSelectedSessions(new Set());
      } else {
        setError(result.error || 'Failed to terminate sessions');
      }
    } catch {
      setError('Failed to terminate sessions');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSessions, makeRequest]);

  const terminateAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await makeRequest('/api/auth/sessions/all', {
        method: 'DELETE',
      });

      if (result.success) {
        setSessions([]);
        setSelectedSessions(new Set());
      } else {
        setError(result.error || 'Failed to terminate all sessions');
      }
    } catch {
      setError('Failed to terminate all sessions');
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest]);

  const toggleSessionSelection = useCallback((sessionId: string) => {
    setSelectedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedSessions(new Set(sessions.map((s) => s.sessionId)));
  }, [sessions]);

  const clearSelection = useCallback(() => {
    setSelectedSessions(new Set());
  }, []);

  const upgradeAndContinue = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await makeRequest('/api/auth/upgrade-token', {
        method: 'POST',
        body: JSON.stringify({ rememberMe: state.rememberMe }),
      });

      if (result.success) {
        router.push(result.redirectUrl || '/overview');
      } else {
        setError(result.error || 'Failed to continue login');
      }
    } catch {
      setError('Failed to continue login');
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest, state.rememberMe, router]);

  return {
    sessions,
    isLoading,
    error,
    selectedSessions,
    terminateSession,
    terminateSelected,
    terminateAll,
    toggleSessionSelection,
    selectAll,
    clearSelection,
    upgradeAndContinue,
  };
}
